import { useState } from "react";
import FormErrors from "../components/FormErrors";
import { login } from "../library/allauth";
import { useConfig, useAuth } from "../context/AuthContext";
import XRoadz_Logo from "../img/XRoadz_Logo.png";
import { useNavigate } from "react-router-dom";

export default function Login() {
  const [formData, setFormData] = useState({ username: "", password: "" });
  const [response, setResponse] = useState({ fetching: false, content: null });
  const [showPassword, setShowPassword] = useState(false);
  const config = useConfig();
  const { setUser } = useAuth();
  const navigate = useNavigate();

  function submit() {
    setResponse({ ...response, fetching: true });
    return login({ username: formData.username, password: formData.password })
      .then((content) => {
        console.log("LOGIN RESPONSE:", JSON.stringify(content)); // ← add this

        setResponse((r) => ({ ...r, content }));
        if (content.status === 200 && content.meta?.is_authenticated) {
          setUser(content.data.user);
        } else if (content.status === 401) {
          // Check for pending MFA flow
          const flows = content.data?.flows || [];
          const hasMFA = flows.some((f) => f.id === "mfa_authenticate");
          if (hasMFA) {
            navigate("/login/mfa");
          }
        }
      })
      .catch((e) => {
        setResponse((r) => ({
          ...r,
          content: { errors: [{ message: e.message || "Login failed" }] },
        }));
      })
      .then(() => setResponse((r) => ({ ...r, fetching: false })));
  }

  const handleSubmit = (e) => {
    e.preventDefault();
    submit();
  };

  if (!config || config.loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <p className="text-gray-400 text-sm">Loading...</p>
      </div>
    );
  }

  return (
    <>
      <style>{`
        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(24px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes fadeIn {
          from { opacity: 0; }
          to   { opacity: 1; }
        }
        @keyframes pulse-slow {
          0%, 100% { opacity: 0.15; transform: scale(1); }
          50%       { opacity: 0.25; transform: scale(1.05); }
        }
        .a1 { animation: fadeUp 0.6s ease forwards; }
        .a2 { animation: fadeUp 0.6s ease 0.15s forwards; opacity: 0; }
        .a3 { animation: fadeUp 0.6s ease 0.3s forwards; opacity: 0; }
        .a4 { animation: fadeUp 0.6s ease 0.45s forwards; opacity: 0; }
        .a5 { animation: fadeIn 1s ease forwards; opacity: 0; }
        .pulse { animation: pulse-slow 4s ease-in-out infinite; }
      `}</style>

      <div className="min-h-screen flex">
        {/* Left Panel */}
        <div className="hidden lg:flex w-1/2 flex-col justify-between p-14 pt-20 relative overflow-hidden bg-emerald-700">
          <div className="pulse absolute w-[500px] h-[500px] rounded-full bg-emerald-600 opacity-20 -top-32 -left-32" />
          <div
            className="pulse absolute w-[300px] h-[300px] rounded-full bg-emerald-900 opacity-20 bottom-20 right-0"
            style={{ animationDelay: "2s" }}
          />

          {/* Logo */}
          <div className="relative z-10 a5 flex items-center gap-4 mt-6 ">
            <div className="h-12 bg-white rounded-xl flex items-center justify-center shadow-sm px-3 overflow-hidden">
              <img
                src={XRoadz_Logo}
                alt="XRoadz"
                className="h-7 object-contain"
              />
            </div>
            <div>
              <p className="text-white font-bold text-xl leading-tight">
                XRoadz WebPortal
              </p>
              <p className="text-emerald-200 text-xs opacity-70 tracking-widest uppercase">
                Traffic Analytics & Roadway Safety
              </p>
            </div>
          </div>

          {/* Center */}
          <div className="relative z-10 max-w-md">
            <div className="a1 inline-flex items-center gap-2 px-3 py-1 rounded-full border border-yellow-300 border-opacity-40 mb-6">
              <span className="w-2 h-2 rounded-full bg-yellow-300 animate-pulse" />
              <span className="text-yellow-200 text-xs tracking-widest uppercase font-medium">
                Real-time Traffic Intelligence
              </span>
            </div>
            <h1 className="a2 text-white text-5xl font-black leading-tight mb-6 tracking-tight">
              Safer
              <br />
              Communities.
              <br />
              <span className="text-emerald-200">Smarter</span> Cities.
            </h1>
            <p className="a3 text-emerald-100 text-sm leading-relaxed opacity-80">
              A unified platform for municipal traffic analytics, license plate
              recognition, and roadway safety — powered by A.I.
            </p>

            <div className="a4 flex gap-8 mt-10">
              {[
                { value: "Real-time", label: "Data processing" },
                { value: "A.I. 1", label: "Platform" },
                { value: "24 / 7", label: "Monitoring" },
              ].map((s) => (
                <div key={s.label}>
                  <p className="text-white font-bold text-lg">{s.value}</p>
                  <p className="text-emerald-200 text-xs mt-0.5 opacity-70">
                    {s.label}
                  </p>
                </div>
              ))}
            </div>
          </div>

          <div className="relative z-10 a5">
            <p className="text-emerald-200 text-xs tracking-widest uppercase opacity-50">
              © {new Date().getFullYear()} XRoadz Inc. — Innovating Traffic
              Safety
            </p>
          </div>
        </div>

        {/* Right Panel */}
        <div className="w-full lg:w-1/2 flex items-center justify-center bg-gray-50 px-8 py-12">
          <div className="w-full max-w-lg">
            {/* Mobile logo */}
            <div className="flex lg:hidden justify-center mb-8">
              <img
                src={XRoadz_Logo}
                alt="XRoadz"
                className="h-10 object-contain"
              />
            </div>

            {/* Form card */}
            <div className="bg-white rounded-2xl border border-gray-200 p-12 shadow-sm">
              <div className="mb-8 a1 text-center">
                <h2 className="text-2xl font-bold text-gray-900 mb-1">
                  Sign In
                </h2>
                <p className="text-sm text-gray-400">XRoadz WebPortal</p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-5 a2">
                {/* Username */}
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1.5">
                    Username (email address)
                  </label>
                  <input
                    type="text"
                    value={formData.username}
                    onChange={(e) =>
                      setFormData({ ...formData, username: e.target.value })
                    }
                    placeholder="Enter your email address"
                    required
                    className="w-full px-4 py-3 text-sm text-gray-900 bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent placeholder-gray-400 transition-colors"
                  />
                  {response.content?.errors && (
                    <FormErrors
                      errors={response.content?.errors}
                      param="username"
                    />
                  )}
                </div>

                {/* Password */}
                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <label className="text-xs font-medium text-gray-600">
                      Password
                    </label>
                    <a
                      href="/forgot-password"
                      className="text-xs text-emerald-700 hover:text-emerald-800 transition-colors"
                    >
                      Forgot password?
                    </a>
                  </div>
                  <div className="relative">
                    <input
                      type={showPassword ? "text" : "password"}
                      value={formData.password}
                      onChange={(e) =>
                        setFormData({ ...formData, password: e.target.value })
                      }
                      placeholder="Enter your password"
                      required
                      className="w-full px-4 py-3 text-sm text-gray-900 bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent placeholder-gray-400 transition-colors pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {showPassword ? (
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
                          <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
                          <line x1="1" y1="1" x2="23" y2="23" />
                        </svg>
                      ) : (
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                          <circle cx="12" cy="12" r="3" />
                        </svg>
                      )}
                    </button>
                  </div>
                  {response.content?.errors && (
                    <FormErrors
                      errors={response.content?.errors}
                      param="password"
                    />
                  )}
                </div>

                <button
                  type="submit"
                  disabled={response.fetching}
                  className="w-full py-3 text-sm font-semibold text-white bg-emerald-700 rounded-lg hover:bg-emerald-800 transition-colors disabled:opacity-50"
                >
                  {response.fetching ? "Signing in..." : "Sign in"}
                </button>
              </form>

              <p className="text-xs text-gray-400 text-center mt-6 a3">
                Having trouble?{" "}
                <a
                  href="mailto:bryce.germain@xroadz.ai"
                  className="text-emerald-700 hover:text-emerald-800 transition-colors"
                >
                  Contact your administrator
                </a>
              </p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
